import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import {useQuery} from '@tanstack/react-query';
import {FaCopy, FaDownload, FaFilePdf, FaPaperPlane, FaPlusSquare, FaTrash} from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import Button from '../../../components/button';
import InputForm from '../../../components/input-form';
import SelectComponent from '../../../components/select-component';
import TrainingsService from '../../../services/training-service';
import InputDateTime from '../../../components/input-datetime';
import InputText from "../../../components/input-text";
import general from "../../../utils/general";
import TrainingUsersTable from "../components/training-users-table";

const MySwal = withReactContent(Swal);

const getRequestErrorMessage = (err, fallback) => {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message;

    return AlertMessage[message] ?? message ?? fallback;
};

const initialState = {
    date: null,
    hours: "",
    treinee: "",
    sentry_version: "",
    module: null,
    session_type: null,
    userclient: [],
    init_time: null,
    end_time: null,
    observation: "",
    validity: "12 meses",
};

const TrainingForm = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isGeneratingCertificates, setIsGeneratingCertificates] = useState(false);
    const [generatingUserCertificateIds, setGeneratingUserCertificateIds] = useState([]);
    const [activeSessionIndex, setActiveSessionIndex] = useState(0);
    const wasGeneratingCertificates = useRef(false);

    const { control, handleSubmit, reset, watch, setValue } = useForm({
        defaultValues: {
            trainings: [initialState]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "trainings"
    });

    const watchTrainings = watch("trainings");

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const { data: trainingData, isFetching, refetch } = useQuery({
        queryFn: () => TrainingsService.getById(id).then((data) => {
            Object.keys(data).forEach(key => data[key] = data[key] ?? "");
            return data;
        }),
        queryKey: ['trainings', {id}],
        enabled: !!id,
    });

    const { data: certificateStatus, refetch: refetchCertificateStatus } = useQuery({
        queryFn: () => TrainingsService.certificateStatus(id).then(({data}) => data),
        queryKey: ['training-certificate-status', {id}],
        enabled: !!id,
        refetchInterval: isGeneratingCertificates || generatingUserCertificateIds.length > 0
            ? 60000
            : false,
    });

    useEffect(() => {
        if (certificateStatus?.certificate_generation_in_progress) {
            wasGeneratingCertificates.current = true;
            setIsGeneratingCertificates(true);
            return;
        }

        if (certificateStatus) {
            setIsGeneratingCertificates(false);

            if (wasGeneratingCertificates.current) {
                wasGeneratingCertificates.current = false;
                refetch();
            }

            if (generatingUserCertificateIds.length > 0) {
                const generatedUserIds = new Set(certificateStatus.generated_user_ids ?? []);
                const completedUserIds = generatingUserCertificateIds.filter(userId => generatedUserIds.has(userId));

                if (completedUserIds.length > 0) {
                    const completedUserIdSet = new Set(completedUserIds);

                    setGeneratingUserCertificateIds(current => current.filter(userId => !completedUserIdSet.has(userId)));
                    refetch();
                }
            }
        }
    }, [certificateStatus, generatingUserCertificateIds, refetch]);

    useEffect(() => {
        if (trainingData) {
            const formatedData = trainingData.trainingSessionSelected.map(training => ({
                ...training,
                hours: typeof training.hours === 'string' ? training.hours.substring(0, 5) : training.hours,
                init_time: typeof training.init_time === 'string' ? new Date(training.init_time) : training.init_time,
                end_time: typeof training.end_time === 'string' ? new Date(training.end_time) : training.end_time
            }));

            reset({ trainings: formatedData });
            setActiveSessionIndex(0);
        }
    }, [trainingData, reset]);

    useEffect(() => {
        if (fields.length && activeSessionIndex > fields.length - 1) {
            setActiveSessionIndex(fields.length - 1);
        }
    }, [activeSessionIndex, fields.length]);

    const formatDateToSave = (date) => {
        if (date) {
            if (typeof date === 'object') {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return date.split(" ")[0];
        }
        return null;
    }

    const onSubmit = (formDataObj) => {
        const formData = new FormData();
        formDataObj.trainings.forEach((inputSet, index) => {
            const {
                userclient,
                id: sessionId,
                date,
                init_time,
                end_time,
                ...dynamicFields
            } = inputSet;

            const dataObject = {};

            Object.entries(dynamicFields).forEach(([key, value]) => {
                if (typeof value === 'object') {
                    dataObject[key] = value?.value ?? "";
                } else {
                    dataObject[key] = value;
                }
            });

            const init = general.formatDate(init_time, "hh:mm");
            const end = general.formatDate(end_time, "hh:mm");

            dataObject['userclient'] = userclient?.map(({id, user_id, name, project}) => ({
                id: id ?? null,
                user_id,
                name,
                project: project ?? null,
            })) ?? [];
            dataObject['date'] = formatDateToSave(new Date(date));
            dataObject['init_time'] = init;
            dataObject['end_time'] = end;
            dataObject['hours'] = (init && end) ? general.getTimeDiff(init, end) : null;
            dataObject['training_session_id'] = sessionId || null;

            formData.append(index, JSON.stringify(dataObject));
        });

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? TrainingsService.edit(formData, id) : TrainingsService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/after-sales/trainings/form/${data.id}`);
            })
            .catch((err) => {
                toast.error(getRequestErrorMessage(err, "Não foi possível salvar"));
            });
    }

    const deleteTraining = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir todas as sessões de treinamento?",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir todas as sessões?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({ isConfirmed }) => {
            if (isConfirmed) {
                setIsLoading(true);

                TrainingsService.delete(id)
                    .then(() => {
                        navigate("/after-sales/trainings");
                    })
                    .catch((err) => {
                        toast.error(getRequestErrorMessage(err, "Não foi possível excluir"));
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const handleInputListCopy = (index) => {
        const itemToCopy = watchTrainings[index];
        append({ ...itemToCopy, id: undefined });
        setActiveSessionIndex(fields.length);
    };

    const handleAddTrainingSession = () => {
        append({...initialState});
        setActiveSessionIndex(fields.length);
    }

    const handleInputListRemove = (index) => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir sessão de treinamento?",
            showCancelButton: true,
            text: "Deseja excluir essa sessão de treinamento?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({ isConfirmed }) => {
            if (isConfirmed) {
                remove(index);
                setActiveSessionIndex(prev => {
                    if (fields.length <= 1) return 0;
                    if (prev >= fields.length - 1) return fields.length - 2;
                    if (prev > index) return prev - 1;
                    return prev;
                });
            }
        });
    };

    const getDiff = (init, end, hour) => {
        if (init && end) {
            return general.getTimeDiff(general.formatDate(init, "hh:mm"), general.formatDate(end, "hh:mm"))
        }else{
            return hour
        }
    }

    const certificateGenerationInProgress = isGeneratingCertificates || certificateStatus?.certificate_generation_in_progress;
    const certificateGenerated = certificateStatus?.certificate_generated ?? trainingData?.certificate_generated ?? false;
    const certificateActionsDisabled = !id || certificateGenerationInProgress || !certificateGenerated;
    const userCertificateActionsDisabled = !id || certificateGenerationInProgress;

    const handleGenerateCertificates = () => {
        if (!id) {
            toast.error("Salve o treinamento antes de gerar os certificados.");
            return;
        }

        if (certificateGenerationInProgress) {
            return;
        }

        setIsGeneratingCertificates(true);

        TrainingsService.generateCertificates(id)
            .then(({message, data}) => {
                if (data?.certificate_generation_in_progress) {
                    setIsGeneratingCertificates(true);
                }

                toast.success(message || "Geração de certificados iniciada.");
                refetchCertificateStatus();
            })
            .catch((err) => {
                setIsGeneratingCertificates(false);
                toast.error(getRequestErrorMessage(err, "Não foi possível iniciar a geração dos certificados."));
            });
    }

    const handleGenerateUserCertificate = (user) => {
        if (!id || !user?.id || !user?.user_id || user.certificate_generated === true) {
            return;
        }

        const userId = user.user_id;

        setGeneratingUserCertificateIds(current => (
            current.includes(userId) ? current : [...current, userId]
        ));

        TrainingsService.generateUserCertificate(id, userId)
            .then(({message, data}) => {
                toast.success(message || "Geração do certificado iniciada.");

                if (data?.certificate_generated) {
                    setGeneratingUserCertificateIds(current => current.filter(currentUserId => currentUserId !== userId));
                    refetch();
                }
            })
            .catch((err) => {
                setGeneratingUserCertificateIds(current => current.filter(currentUserId => currentUserId !== userId));
                toast.error(getRequestErrorMessage(err, "Não foi possível iniciar a geração do certificado."));
            });
    }

    const handleDownloadCertificates = () => {
        if (!id) {
            return;
        }

        setIsLoading(true);

        TrainingsService.downloadCertificates(id)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `certificados-treinamento-${id}.zip`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                toast.error(getRequestErrorMessage(err, "Não foi possível baixar os certificados."));
            })
            .finally(() => setIsLoading(false));
    }

    const handleSendCertificates = () => {
        if (!id) {
            return;
        }

        setIsLoading(true);

        TrainingsService.sendCertificates(id)
            .then(({message, data}) => {
                const skipped = data?.skipped?.length ?? 0;
                const failed = data?.failed?.length ?? 0;

                if (skipped || failed) {
                    toast.warning(`${message} Sem e-mail: ${skipped}. Falhas: ${failed}.`);
                    return;
                }

                toast.success(message || "Certificados enviados com sucesso.");
            })
            .catch((err) => {
                toast.error(getRequestErrorMessage(err, "Não foi possível enviar os certificados."));
            })
            .finally(() => setIsLoading(false));
    }
    const handleSendUserCertificate = (user) => {
        if (!id || !user?.user_id || user.certificate_generated !== true) {
            return;
        }

        TrainingsService.sendUserCertificate(id, user.user_id)
            .then(({message}) => {
                toast.success(message || "Certificado enviado com sucesso.");
            })
            .catch((err) => {
                toast.error(getRequestErrorMessage(err, "Não foi possível enviar o certificado."));
            });
    }

    const handleDownloadUserCertificate = (user) => {
        if (!id || !user?.user_id || user.certificate_generated !== true) {
            return;
        }

        setIsLoading(true);

        TrainingsService.downloadUserCertificate(id, user.user_id)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `certificado-${user.name ?? user.user_id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => {
                toast.error(getRequestErrorMessage(err, "Não foi possível baixar o certificado."));
            })
            .finally(() => setIsLoading(false));
    }

    const currentValues = watchTrainings?.[activeSessionIndex] || {};
    const selectedSessionType = currentValues.session_type?.value || currentValues.session_type;
    const hasSelectedSessionType = selectedSessionType !== undefined && selectedSessionType !== null && selectedSessionType !== "";

    return (
        <TemplateDefault title="Cadastro de Treinamento" isLoading={isFetching || isLoading}>

            <form className="flex h-[calc(100dvh-71px)] min-h-0 flex-col" onSubmit={handleSubmit(onSubmit)}>
                {fields[activeSessionIndex] && (() => {
                    const index = activeSessionIndex;
                    return (
                        <div
                            key={fields[index].id}
                            className={hasSelectedSessionType ? "min-h-0 min-w-0 flex-1" : "min-w-0 shrink-0"}
                        >


                            <Card
                                wrapperClass={hasSelectedSessionType ? "mt-2 flex h-full min-w-0 flex-col" : "mt-2 min-w-0"}
                                bodyClass={hasSelectedSessionType ? "flex min-h-0 flex-1 flex-col" : undefined}
                            >
                                <div className="flex flex-wrap items-center justify-end gap-4">
                                    <Button
                                        type="button"
                                        target="primary"
                                        disabled={!id || certificateGenerationInProgress}
                                        onClick={handleGenerateCertificates}
                                    >
                                        <FaFilePdf /> {certificateGenerationInProgress ? "Gerando certificados" : "Gerar certificados"}
                                    </Button>

                                    <Button
                                        type="button"
                                        target="primary"
                                        disabled={certificateActionsDisabled}
                                        onClick={handleSendCertificates}
                                        title="Enviar certificado"
                                    >
                                        <FaPaperPlane />
                                    </Button>

                                    <Button
                                        type="button"
                                        target="primary"
                                        disabled={certificateActionsDisabled}
                                        onClick={handleDownloadCertificates}
                                        title="Baixar certificado"
                                    >
                                        <FaDownload />
                                    </Button>
                                </div>
                                <div className="flex flex-wrap items-end gap-1 px-2 pt-3">
                                    {fields.map((field, index) => (
                                        <button
                                            key={field.id}
                                            type="button"
                                            className={
                                                activeSessionIndex === index
                                                    ? "relative z-10 -mb-[1px] flex h-[34px] items-center justify-center rounded-t border border-b-white border-gray-400 bg-white px-4 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                                    : "flex h-[32px] items-center justify-center rounded-t border border-b-white border-gray-400 bg-gray-100 px-4 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                            }
                                            onClick={() => setActiveSessionIndex(index)}
                                        >
                                            Sessão {index + 1}
                                        </button>
                                    ))}
                                    <button type="button" className={"flex h-[32px] items-center justify-center rounded-t border border-b-white border-gray-400 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"}
                                            onClick={handleAddTrainingSession}
                                            title="Adicionar sessão">
                                        <FaPlusSquare />
                                    </button>
                                </div>
                                <div className={hasSelectedSessionType ? "min-h-0 flex-1 rounded-lg border border-gray-400 bg-white p-2" : "rounded-lg border border-gray-400 bg-white p-2"}>
                                    <div
                                        className={
                                            hasSelectedSessionType
                                                ? "grid h-full min-h-0 grid-cols-1 gap-4 overflow-y-auto pt-3 xl:grid-cols-12 p-1"
                                                : "grid grid-cols-1 gap-4 pt-3 xl:grid-cols-12 p-1"
                                        }
                                    >

                                        <Controller
                                            name={`trainings.${index}.session_type`}
                                            control={control}
                                            render={({ field }) => (
                                                <SelectComponent
                                                    col="xl:col-span-2 min-w-0 "
                                                    instanceId={`session_type_${index}`}
                                                    label="Tipo"
                                                    options={[
                                                        { value: "Multiway", label: "Multiway" },
                                                        { value: "on-site", label: "on-site" },
                                                        { value: "EAD", label: "EAD" },
                                                        { value: "Webinar", label: "Webinar" },
                                                        { value: "Integradores", label: "Integradores" }
                                                    ]}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    name={`session_type_${index}`}
                                                />
                                            )}
                                        />

                                        <div className="flex justify-end gap-4 xl:col-span-10">
                                            <Button type="button" onClick={() => handleInputListCopy(index)}>
                                                <FaCopy />
                                            </Button>
                                            {fields.length > 1 && (
                                                <Button type="button" target="danger" onClick={() => handleInputListRemove(index)}>
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>

                                        {hasSelectedSessionType &&
                                            <>
                                                <Card title={"Dados"} wrapperClass="min-h-0 min-w-0 xl:col-span-6 xl:flex xl:flex-col" bodyClass="xl:min-h-0 xl:flex-1">
                                                    <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
                                                        <Controller
                                                            name={`trainings.${index}.date`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <InputDateTime
                                                                    id={`date_${index}`}
                                                                    label={"Data"}
                                                                    selected={field.value}
                                                                    onChange={field.onChange}
                                                                    col="md:col-span-2"
                                                                />
                                                            )}
                                                        />

                                                        <Controller
                                                            name={`trainings.${index}.treinee`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <InputForm
                                                                    id={`treinee_${index}`}
                                                                    name={`treinee`}
                                                                    label="Instrutor"
                                                                    value={field.value}
                                                                    col={selectedSessionType === "Integradores" ? "md:col-span-2" : "md:col-span-3"}
                                                                    type="text"
                                                                    onChange={field.onChange}
                                                                />
                                                            )}
                                                        />

                                                        {selectedSessionType !== "Integradores" &&
                                                            <>
                                                                <Controller
                                                                    name={`trainings.${index}.module`}
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <SelectComponent
                                                                            col="md:col-span-3 min-w-0"
                                                                            instanceId={`module_${index}`}
                                                                            label="Módulo"
                                                                            options={[
                                                                                { value: "Abordagem", label: "Abordagem" },
                                                                                { value: "Administração", label: "Administração" },
                                                                                { value: "Despacho", label: "Despacho" },
                                                                                { value: "Gestão", label: "Gestão" },
                                                                                { value: "Inteligência", label: "Inteligência" },
                                                                                { value: "Operacional", label: "Operacional" },
                                                                                { value: "Reconhecimento facial", label: "Reconhecimento facial" },
                                                                                { value: "SOS Cidadão", label: "SOS Cidadão" },
                                                                                { value: "VMS", label: "VMS" },
                                                                                { value: "Vigia Mais MT", label: "Vigia Mais MT" },
                                                                            ]}
                                                                            value={field.value}
                                                                            onChange={field.onChange}
                                                                            name={`module_${index}`}
                                                                        />
                                                                    )}
                                                                />

                                                                <Controller
                                                                    name={`trainings.${index}.sentry_version`}
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <InputForm
                                                                            id={`sentry_version_${index}`}
                                                                            name={`sentry_version`}
                                                                            label="Versão do Sentry"
                                                                            value={field.value}
                                                                            col="md:col-span-2"
                                                                            type="text"
                                                                            onChange={field.onChange}
                                                                        />
                                                                    )}
                                                                />
                                                            </>
                                                        }

                                                        <Controller
                                                            name={`trainings.${index}.init_time`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <InputDateTime
                                                                    id={`init_time_${index}`}
                                                                    label={"Horário inicial"}
                                                                    selected={field.value}
                                                                    onlyTime
                                                                    onChange={field.onChange}
                                                                    col="md:col-span-2"
                                                                />
                                                            )}
                                                        />

                                                        <Controller
                                                            name={`trainings.${index}.end_time`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <InputDateTime
                                                                    id={`end_time_${index}`}
                                                                    label={"Horário final"}
                                                                    selected={field.value}
                                                                    onlyTime
                                                                    onChange={field.onChange}
                                                                    col="md:col-span-2"
                                                                />
                                                            )}
                                                        />

                                                        <InputForm
                                                            id={`hours_${index}`}
                                                            name={`hours_${index}`}
                                                            label="Duração"
                                                            value={getDiff(currentValues.init_time, currentValues.end_time, currentValues.hours)}
                                                            col="md:col-span-2"
                                                            disabled
                                                        />

                                                        {selectedSessionType === "Integradores" &&
                                                            <Controller
                                                                name={`trainings.${index}.validity`}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <InputForm
                                                                        id={`validity_${index}`}
                                                                        name={`validity`}
                                                                        label="Validade"
                                                                        value={field.value}
                                                                        col="md:col-span-2"
                                                                        type="text"
                                                                        onChange={field.onChange}
                                                                    />
                                                                )}
                                                            />
                                                        }

                                                        <Controller
                                                            name={`trainings.${index}.observation`}
                                                            control={control}
                                                            render={({ field }) => (
                                                                <InputText
                                                                    height="min-h-[140px] xl:h-[calc(100dvh-540px)] xl:min-h-[120px]"
                                                                    id={`observation_${index}`}
                                                                    name={`observation_${index}`}
                                                                    label="Observação"
                                                                    col="md:col-span-8"
                                                                    value={field.value}
                                                                    onChange={field.onChange}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </Card>

                                                <div className="min-h-0 min-w-0 xl:col-span-6">
                                                    <TrainingUsersTable
                                                        users={currentValues.userclient ?? []}
                                                        onChange={(users) => setValue(`trainings.${index}.userclient`, users)}
                                                        setIsLoading={setIsLoading}
                                                        certificateActionsDisabled={userCertificateActionsDisabled}
                                                        generatingCertificateUserIds={generatingUserCertificateIds}
                                                        onGenerateCertificate={handleGenerateUserCertificate}
                                                        onSendCertificate={handleSendUserCertificate}
                                                        onDownloadCertificate={handleDownloadUserCertificate}
                                                    />
                                                </div>
                                            </>

                                        }

                                    </div>
                                </div>
                            </Card>
                        </div>
                    );
                })()}

                <Card wrapperClass="mt-4 shrink-0">
                    <div className="flex justify-end gap-4">
                        {
                            id &&
                            <Button type="button" onClick={deleteTraining} target="danger">
                                Excluir
                            </Button>
                        }
                        <Button type="button" onClick={() => navigate("/after-sales/trainings")} target="warning">
                            Cancelar
                        </Button>

                        <Button type="submit" target="success">
                            Salvar
                        </Button>
                    </div>
                </Card>
            </form>
        </TemplateDefault>
    );
};

export default TrainingForm;

